import { useT, type Translate } from "../../../shared/i18n/i18n";
import { skipToken, useQuery } from "@tanstack/react-query";
import { createContext, useEffect, useMemo, useRef, useState } from "react";
import type { BoardItemView, BoardView } from "../../../entities/board/model/use-boards";
import { trpc as trpcClient, useTRPC } from "../../../shared/api/trpc";

export type SprintReplayView = Awaited<ReturnType<typeof trpcClient.sprints.replay.query>>;

type ViewQuery = BoardView["views"][number]["query"];

export type ReplayMomentKind =
  | "carried"
  | "closed"
  | "completed"
  | "planned"
  | "removed"
  | "reopened"
  | "scope-added"
  | "stalled"
  | "started"
  | "status";

export type ReplayMoment = {
  at: number;
  kind: ReplayMomentKind;
  message: string;
  tone: "neutral" | "positive" | "warning";
  workObjectId: string | null;
};

// While replay is active, cards read "time in current status at moment T"
// labels from here (keyed by work object id); null outside of replay.
export const SprintReplayStatusSinceContext = createContext<Map<string, string> | null>(null);

export function formatReplayDuration(elapsedMs: number): string {
  const hours = elapsedMs / (60 * 60 * 1000);
  if (hours < 1) return `${Math.max(1, Math.round(elapsedMs / 60_000))}m`;
  if (hours < 48) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

// A step is a point in time where something actually happened — playback
// jumps from step to step, skipping the quiet stretches in between.
export type ReplayStep = {
  at: number;
  moments: ReplayMoment[];
};

export type ReplayFrame = {
  items: BoardItemView[];
  query: ViewQuery;
  // When the current status run of each visible item began (ms epoch).
  statusSince: Map<string, number>;
};

export type ReplayEngine = {
  data: SprintReplayView;
  end: number;
  eventsByObject: Map<string, Array<{ at: number; statusCategory: string; statusId: string | null }>>;
  memberships: Array<{ addedAt: number; removedAt: number | null; workObjectId: string }>;
  start: number;
  steps: ReplayStep[];
};

const dayMs = 24 * 60 * 60 * 1000;
// Wall-clock allowance per estimated hour: ~6 focused hours make a workday,
// so an 8h estimate is given ~32h of calendar time before an item counts as
// stalled. Non-hour estimates fall back to a share of the sprint length.
const wallClockPerEstimateHour = dayMs / 6;
const stalledSprintShare = 0.4;

const categoryLabels: Record<string, string> = {
  active: "In progress",
  closed: "Closed",
  done: "Done",
  not_started: "To do",
};

function isDoneCategory(category: string) {
  const t = useT();
  return category === "done" || category === "closed";
}

function isMember(engine: Pick<ReplayEngine, "memberships">, workObjectId: string, at: number) {
  return engine.memberships.some(
    (membership) =>
      membership.workObjectId === workObjectId &&
      membership.addedAt <= at &&
      (membership.removedAt === null || membership.removedAt > at),
  );
}

function lastEventAt(engine: Pick<ReplayEngine, "eventsByObject">, workObjectId: string, at: number) {
  let latest: { at: number; statusCategory: string; statusId: string | null } | null = null;
  for (const event of engine.eventsByObject.get(workObjectId) ?? []) {
    if (event.at > at) break;
    latest = event;
  }
  return latest;
}

export function buildReplayEngine(data: SprintReplayView, now: number, t: Translate): ReplayEngine {
  const start = Date.parse(`${data.sprint.startsAt}T00:00:00`);
  const plannedEnd = Date.parse(`${data.sprint.endsAt}T23:59:59.999`);
  const closedAt = data.sprint.closedAt ? new Date(data.sprint.closedAt).getTime() : null;
  // A closed sprint replays until its closing moment (which may run past the
  // planned end); an open one until "now" — nothing later is known yet.
  const end = Math.max(start + 1, closedAt ?? Math.min(now, Math.max(plannedEnd, now > plannedEnd ? now : 0)));

  const memberships = data.memberships.map((membership) => ({
    addedAt: new Date(membership.addedAt).getTime(),
    removedAt: membership.removedAt ? new Date(membership.removedAt).getTime() : null,
    workObjectId: membership.workObjectId,
  }));
  const eventsByObject = new Map<string, Array<{ at: number; statusCategory: string; statusId: string | null }>>();
  for (const event of data.events) {
    const list = eventsByObject.get(event.workObjectId) ?? [];
    list.push({ at: new Date(event.createdAt).getTime(), statusCategory: event.statusCategory, statusId: event.statusId });
    eventsByObject.set(event.workObjectId, list);
  }
  for (const list of eventsByObject.values()) list.sort((a, b) => a.at - b.at);

  const titles = new Map(data.items.map((item) => [item.workObject.id, item.workObject.title]));
  const statusNames = new Map(data.statuses.map((status) => [status.id, status.name]));
  const titleOf = (workObjectId: string) => titles.get(workObjectId) ?? "Work item";
  const context = { eventsByObject, memberships };

  const moments: ReplayMoment[] = [];
  // Same commitment window as the report: planned within the first day.
  const commitCutoff = start + dayMs;
  const committed = memberships.filter(
    (membership) => membership.addedAt <= commitCutoff && (membership.removedAt === null || membership.removedAt > membership.addedAt),
  ).length;
  moments.push({
    at: start,
    kind: "started",
    message: `Sprint started — ${committed} ${committed === 1 ? "item" : "items"} committed`,
    tone: "neutral",
    workObjectId: null,
  });
  if (closedAt && closedAt <= end) {
    moments.push({ at: closedAt, kind: "closed", message: t("sprint.closedMoment"), tone: "neutral", workObjectId: null });
  }

  for (const membership of memberships) {
    if (membership.addedAt > start && membership.addedAt <= end) {
      const midSprint = membership.addedAt > commitCutoff;
      moments.push({
        at: membership.addedAt,
        kind: midSprint ? "scope-added" : "planned",
        message: midSprint ? `“${titleOf(membership.workObjectId)}” added mid-sprint — scope grew` : `“${titleOf(membership.workObjectId)}” planned into the sprint`,
        tone: midSprint ? "warning" : "neutral",
        workObjectId: membership.workObjectId,
      });
    }
    if (membership.removedAt !== null && membership.removedAt > start && membership.removedAt <= end) {
      const atClose = closedAt !== null && Math.abs(membership.removedAt - closedAt) < 5_000;
      moments.push({
        at: membership.removedAt,
        kind: atClose ? "carried" : "removed",
        message: atClose
          ? `“${titleOf(membership.workObjectId)}” left unfinished — carried out at close`
          : `“${titleOf(membership.workObjectId)}” removed from the sprint`,
        tone: "warning",
        workObjectId: membership.workObjectId,
      });
    }
  }

  for (const [workObjectId, events] of eventsByObject) {
    let previousCategory: string | null = null;
    let activeSince: number | null = null;
    const estimateHours = data.primaryEstimate?.unit === "h" ? data.primaryEstimate.values[workObjectId] ?? null : null;
    const stalledAfter =
      estimateHours && estimateHours > 0
        ? Math.max(estimateHours * wallClockPerEstimateHour, dayMs)
        : Math.max((plannedEnd - start) * stalledSprintShare, dayMs);

    const flushActiveRun = (until: number) => {
      if (activeSince === null) return;
      const crossing = activeSince + stalledAfter;
      if (crossing < until && crossing > start && crossing <= end && isMember(context, workObjectId, crossing)) {
        const days = Math.max(1, Math.round((crossing - activeSince) / dayMs));
        moments.push({
          at: crossing,
          kind: "stalled",
          message:
            estimateHours && estimateHours > 0
              ? `“${titleOf(workObjectId)}” in progress ${days}d — over its ${estimateHours}h estimate`
              : `“${titleOf(workObjectId)}” in progress ${days}d — stalling`,
          tone: "warning",
          workObjectId,
        });
      }
      activeSince = null;
    };

    for (const event of events) {
      if (event.statusCategory !== "active") flushActiveRun(Math.min(event.at, end));
      else if (previousCategory !== "active") activeSince = event.at;

      if (event.at > start && event.at <= end && previousCategory !== null && isMember(context, workObjectId, event.at)) {
        const wasDone = isDoneCategory(previousCategory);
        const nowDone = isDoneCategory(event.statusCategory);
        const statusLabel =
          (event.statusId ? statusNames.get(event.statusId) : null) ?? categoryLabels[event.statusCategory] ?? event.statusCategory;
        moments.push({
          at: event.at,
          kind: nowDone && !wasDone ? "completed" : wasDone && !nowDone ? "reopened" : "status",
          message:
            nowDone && !wasDone
              ? `“${titleOf(workObjectId)}” completed`
              : wasDone && !nowDone
                ? `“${titleOf(workObjectId)}” reopened`
                : `“${titleOf(workObjectId)}” → ${statusLabel}`,
          tone: nowDone && !wasDone ? "positive" : wasDone && !nowDone ? "warning" : "neutral",
          workObjectId,
        });
      }
      previousCategory = event.statusCategory;
    }
    if (previousCategory === "active") flushActiveRun(end);
  }

  moments.sort((a, b) => a.at - b.at);
  const steps: ReplayStep[] = [];
  for (const moment of moments) {
    const current = steps[steps.length - 1];
    if (current && moment.at - current.at <= 1_500) current.moments.push(moment);
    else steps.push({ at: moment.at, moments: [moment] });
  }

  return { data, end, eventsByObject, memberships, start, steps };
}

// Rebuild the board state at moment T: which items were in the sprint and
// which status each one had. Statuses resolve to the board's current columns
// (an archived status falls back to the first column of its category).
export function replayFrameAt(engine: ReplayEngine, at: number, board: BoardView, view: BoardView["views"][number]): ReplayFrame {
  const statusById = new Map(board.statuses.map((status) => [status.id, status]));
  const catalogById = new Map(engine.data.statuses.map((status) => [status.id, status]));
  const firstByCategory = new Map<string, BoardView["statuses"][number]>();
  for (const status of board.statuses) {
    if (!firstByCategory.has(status.category)) firstByCategory.set(status.category, status);
  }

  const items: BoardItemView[] = [];
  const statusSince = new Map<string, number>();
  for (const item of engine.data.items) {
    const workObjectId = item.workObject.id;
    if (!isMember(engine, workObjectId, at)) continue;
    const event = lastEventAt(engine, workObjectId, at);
    const category = event?.statusCategory ?? (event?.statusId ? catalogById.get(event.statusId)?.category : null) ?? "not_started";
    const resolved = (event?.statusId ? statusById.get(event.statusId) : null) ?? firstByCategory.get(category) ?? board.statuses[0];
    if (!resolved) continue;
    items.push({ ...item, status: resolved });

    // The start of the current status run: consecutive events with the same
    // status id extend one run.
    let runStart: number | null = null;
    let previousStatusId: string | null = null;
    for (const entry of engine.eventsByObject.get(workObjectId) ?? []) {
      if (entry.at > at) break;
      if (entry.statusId !== previousStatusId) runStart = entry.at;
      previousStatusId = entry.statusId;
    }
    if (runStart !== null) statusSince.set(workObjectId, runStart);
  }

  return {
    items,
    query: {
      groups: synthesizeGroups(view, board, items),
      nextCursor: null,
      workObjectIds: items.map((item) => item.workObject.id),
    },
    statusSince,
  };
}

// Mirrors the server's group shape so buildBoardItemGroups renders replay
// frames exactly like live pages. Only status history is journaled, so
// non-status groupings bucket by the item's current field value.
function synthesizeGroups(view: BoardView["views"][number], board: BoardView, items: BoardItemView[]): ViewQuery["groups"] {
  const groupBy = view.config.groupBy;
  if (!groupBy?.startsWith("field:")) return [];
  const fieldId = groupBy.slice("field:".length);
  if (fieldId === board.statusFieldDefinitionId) {
    return board.statuses.map((status) => {
      const workObjectIds = items.filter((item) => item.status.id === status.id).map((item) => item.workObject.id);
      return { key: `status:${status.id}`, nextCursor: null, totalCount: workObjectIds.length, value: status.id, workObjectIds };
    });
  }
  const buckets = new Map<string, string[]>();
  for (const item of items) {
    const raw = item.fieldValues.find((value) => value.fieldDefinitionId === fieldId)?.value ?? null;
    const values = Array.isArray(raw) ? raw.map(String) : raw === null || raw === "" ? [""] : [String(raw)];
    for (const value of values.length > 0 ? values : [""]) {
      buckets.set(value, [...(buckets.get(value) ?? []), item.workObject.id]);
    }
  }
  return [...buckets.entries()].map(([value, workObjectIds]) => ({
    key: value === "" ? `field:${fieldId}:__empty__` : `field:${fieldId}:${value}`,
    nextCursor: null,
    totalCount: workObjectIds.length,
    value,
    workObjectIds,
  }));
}

export function useSprintReplay({
  board,
  enabled,
  sprintId,
  view,
}: {
  board: BoardView | null;
  enabled: boolean;
  sprintId: string | null;
  view: BoardView["views"][number] | null;
}) {
  const t = useT();
  const trpc = useTRPC();
  const replayQuery = useQuery(trpc.sprints.replay.queryOptions(enabled && sprintId ? { sprintId } : skipToken));
  const data = enabled ? replayQuery.data ?? null : null;
  const engine = useMemo(() => (data ? buildReplayEngine(data, Date.now(), t) : null), [data, t]);

  const [time, setTime] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  // "day" narrows the working range to the last 24h — the stand-up preset.
  const [range, setRange] = useState<"day" | "sprint">("sprint");
  const canUseDayRange = Boolean(engine && engine.end - engine.start > dayMs);
  const windowStart = engine ? (range === "day" && canUseDayRange ? engine.end - dayMs : engine.start) : null;
  const windowEnd = engine?.end ?? null;

  // A fresh session starts at the sprint's first moment and plays through.
  const engineKey = engine ? `${engine.data.sprint.id}:${engine.start}:${engine.end}` : null;
  const lastEngineKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (engineKey === lastEngineKeyRef.current) return;
    lastEngineKeyRef.current = engineKey;
    if (engineKey && engine) {
      setTime(engine.start);
      setPlaying(true);
    } else {
      setTime(null);
      setPlaying(false);
    }
  }, [engine, engineKey]);

  const at = time ?? engine?.start ?? null;
  const stepIndex = useMemo(() => {
    if (!engine || at === null) return -1;
    let index = -1;
    for (const [position, step] of engine.steps.entries()) {
      if (step.at <= at) index = position;
      else break;
    }
    return index;
  }, [at, engine]);

  // Steps within the working range — everything (bar markers, the history
  // log, playback, prev/next) sees only this slice.
  const visibleSteps = useMemo(() => {
    if (!engine || windowStart === null) return [];
    return engine.steps.filter((step) => step.at >= windowStart);
  }, [engine, windowStart]);

  useEffect(() => {
    if (!playing || !engine || at === null) return;
    const next = visibleSteps.find((step) => step.at > at);
    if (!next) {
      setPlaying(false);
      return;
    }
    const current = stepIndex >= 0 ? engine.steps[stepIndex] : null;
    const dwell = current && current.moments.some((moment) => moment.tone !== "neutral") ? 2_000 : 1_200;
    const timer = window.setTimeout(() => setTime(next.at), dwell);
    return () => window.clearTimeout(timer);
  }, [at, engine, playing, stepIndex, visibleSteps]);

  const frame = useMemo(() => {
    if (!engine || at === null || !board || !view || view.type === "timeline") return null;
    return replayFrameAt(engine, at, board, view);
  }, [at, board, engine, view]);

  function scrub(next: number) {
    if (!engine || windowStart === null || windowEnd === null) return;
    setPlaying(false);
    setTime(Math.min(windowEnd, Math.max(windowStart, next)));
  }

  function changeRange(next: "day" | "sprint") {
    if (!engine || next === range) return;
    setRange(next);
    const nextStart = next === "day" && canUseDayRange ? engine.end - dayMs : engine.start;
    setTime(nextStart);
    setPlaying(true);
  }

  // Prev/next navigation over key moments only (anything non-neutral).
  const keySteps = useMemo(
    () => visibleSteps.filter((step) => step.moments.some((moment) => moment.tone !== "neutral")),
    [visibleSteps],
  );
  const previousKeyStep = at === null ? undefined : [...keySteps].reverse().find((step) => step.at < at);
  const nextKeyStep = at === null ? undefined : keySteps.find((step) => step.at > at);

  function jumpToPreviousKey() {
    if (previousKeyStep) scrub(previousKeyStep.at);
  }

  function jumpToNextKey() {
    if (nextKeyStep) scrub(nextKeyStep.at);
  }

  function togglePlay() {
    if (!engine || at === null || windowStart === null) return;
    if (playing) {
      setPlaying(false);
      return;
    }
    const hasFutureStep = visibleSteps.some((step) => step.at > at);
    if (!hasFutureStep) setTime(windowStart);
    setPlaying(true);
  }

  return {
    activeStepAt: engine && stepIndex >= 0 ? engine.steps[stepIndex]?.at ?? null : null,
    canUseDayRange,
    caption: engine && stepIndex >= 0 ? engine.steps[stepIndex]?.moments ?? [] : [],
    changeRange,
    engine,
    frame,
    hasNextKey: Boolean(nextKeyStep),
    hasPreviousKey: Boolean(previousKeyStep),
    isLoading: enabled && replayQuery.isLoading,
    jumpToNextKey,
    jumpToPreviousKey,
    playing,
    range,
    scrub,
    time: at,
    togglePlay,
    visibleSteps,
    windowEnd,
    windowStart,
  };
}
